import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DecodedToken, LoginResponse, User } from '../models/user';
import { DataService } from './data.service';
import { EncryptionService } from './encryption.service';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import Bowser from 'bowser';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@env/environment';
import { Observable, map, catchError, of, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private api = environment.StagingUrl;

  private isLoggingIn = false; // A flag to prevent multiple login requests
  private readonly userKey = 'userkey';
  private readonly tokenKey = 'jwtToken';
  decode!: DecodedToken;
  unameValue!: string;
  public currentUser!: any;
  private deviceCred: {
    ipAddress?: string;
    browserName?: string;
    browserVersion?: string;
  } = {};
  constructor(
    private http: HttpClient,
    private router: Router,
    private encService: EncryptionService,
    private cookies: CookieService,
    private toastr: ToastrService,
    private dataServ: DataService
  ) {
    if (this.JWTToken) {
      this.decode = jwtDecode(this.JWTToken);
      this.cookies.delete('roleId', '/dashboard');
      // this.cookies.set('roleId', this.decode.RoleId);

      this.cookies.set('roleId', this.decode.RoleId, {
        path: '/',
        secure: true,
        sameSite: 'Lax',
      });
    }
    this.dataServ.currentUser.subscribe((data: User) => {
      this.currentUser = data;
    });
  }

  // login(uname: string, key: string, luser: boolean,reCaptchaResponse:any): void {
  login(uname: string, key: string, luser: boolean): void {
    if (this.isLoggingIn) {
      return; // Prevent multiple login attempts
    }
    this.isLoggedOut = false;
    this.isLoggingIn = true;
    this.unameValue = uname;
    this.dataServ.processing.next(true);

    const payload = {
      tokenNo: this.encService.encrypt(uname),
      password: this.encService.encrypt(key),
      loggedUser: luser,
      // captcha: reCaptchaResponse,
      ...this.deviceCred,
    };

    this.http
      .post<LoginResponse>(`${this.api}account/login`, payload)
      .subscribe(
        (data: LoginResponse) => {
          this.dataServ.processing.next(false);
          this.isLoggingIn = false; // Reset the flag on success or error

          if (
            data.status === 500 &&
            data.message.includes('Account is locked')
          ) {
            localStorage.setItem('lockoutMessage', data.message); // Store lockout message
            this.toastr.error(data.message, 'Error');
            return;
          }

          if (data.status === 200) {
            this.decode = jwtDecode(data.token);

            localStorage.removeItem('lockoutMessage'); // Clear lockout flag on successful login

            this.currentUser = {
              codeName: data.codeName,
              name: this.decode.Name,
              role: this.encService.encrypt(this.decode.RoleId),
              username: data.username,
              tokenNo: this.encService.encrypt(this.unameValue),
              id: data.id,
            };

            this.dataServ.currentUser.next(this.currentUser);
            const decrole = Number(
              this.encService.decrypt(this.currentUser.role)
            );
            this.dataServ.roleOfLoggedInUser.next(decrole);

            this.cookies.set(
              this.userKey,
              JSON.stringify(this.currentUser),
              undefined,
              '/'
            );

            this.cookies.set(this.tokenKey, data.token, undefined, '/');


            // this.validateSession().subscribe((isValid) => {
            //   if (isValid) {

            //     setTimeout(() => {
            //       this.redirectUser(decrole)
            //         .then(() => this.toastr.success('Logged in successfully'))
            //         .catch(() => this.toastr.error('Error while logging in'));
            //     }, 200); // 100ms delay gives browser time to write cookie
            //   }
            // });

            this.dataServ.currentUser.pipe(take(1)).subscribe(() => {
              this.validateSession().subscribe((isValid) => {
                if (isValid) {
                  setTimeout(() => {
                    this.redirectUser(decrole)
                      .then(() => this.toastr.success('Logged in successfully'))
                      .catch(() => this.toastr.error('Error while logging in'));
                  }, 100);
                }
              });
            });
          } else {
            this.toastr.error(data.message, 'Error');
            this.dataServ.resetLogin.next(true);
          }
        },
        (e: HttpErrorResponse) => {
          this.dataServ.processing.next(false);
          this.isLoggingIn = false; // Reset the flag on error
          this.toastr.error('Wrong Credentials', 'Error');
        }
      );
  }

  validateSession(): Observable<boolean> {
    return this.http
      .get<{ message: string }>(`${this.api}account/getByToken`)
      .pipe(
        map((response) => {
          return true; // Session valid
        }),
        catchError((error) => {
          if (error.status === 401) {
            this.toastr.error('Session invalid', 'Error');
            this.logOut();
          }
          return of(false); // Session invalid
        })
      );
  }

  redirectUser(userRole: number): Promise<void> {
    return new Promise((resolve, reject) => {
      if (userRole === 2) {
        this.router
          .navigate(['/dashboard'])
          .then(() => resolve())
          .catch((error) => reject(error));
      } else if (userRole === 4) {
        this.router
          .navigate(['/dashboardZonalHead'])
          .then(() => resolve())
          .catch((error) => reject(error));
      } else if (userRole === 5) {
        this.router
          .navigate(['/dashboardFinance'])
          .then(() => resolve())
          .catch((error) => reject(error));
      } else if (userRole === 7) {
        this.router
          .navigate(['/pmisLandingPage'])
          .then(() => resolve())
          .catch((error) => reject(error));
      } else if (userRole === 6) {
        this.router
          .navigate(['/nshLandingPage'])
          .then(() => resolve())
          .catch((error) => reject(error));
      } else if (userRole === 8) {
        this.router
          .navigate(['/cosmLandingPage'])
          .then(() => resolve())
          .catch((error) => reject(error));
      } else {
        reject(new Error('Invalid role'));
      }
    });
  }

  isLoggedOut: boolean = false;
  logOut() {
    this.isLoggedOut = true;
    try {
      const tokenno = this.JWTToken;

      if (!tokenno) {
        this.logOutWithoutCall();
        // this.toastr.success('Logout Successfully');
        this.router.navigate(['/login'], { replaceUrl: true }); // Navigate to login and replace the current URL
        // this.toastr.clear();
        setTimeout(() => {
          // window.location.reload();
        }, 1000);
        return;
      }

      this.http.get(`${this.api}account/logout`).subscribe({
        next: (data) => {
          localStorage.removeItem('selectedDealers');
          this.dataServ.processing.next(false);
          this.logOutWithoutCall();
          this.router.navigate(['/login'], { replaceUrl: true }); // Navigate to login and replace the current URL
          this.toastr.clear();
          this.toastr.success('Logout Successfully');
          setTimeout(() => {
            // window.location.reload();
          }, 1000);
        },
        error: (e) => {
          this.dataServ.processing.next(false);
          this.logOutWithoutCall();
        },
      });
    } catch (e: any) {
      this.dataServ.processing.next(false);
      this.logOutWithoutCall();
    }
  }

  logOutWithoutCall(navigation: boolean = true) {
    this.cookies.delete(this.tokenKey, '/');
    this.cookies.delete(this.userKey, '/');
    this.dataServ.currentUser.next({
      name: '',
      role: '',
      codeName: '',
      username: '',
      id: '',
    });

    if (navigation == true) this.router.navigate(['/login']);
  }

  setUserCredentials() {
    const data = this.cookies.get(this.userKey);
    if (data) this.dataServ.currentUser.next({ ...JSON.parse(data) });
    else this.dataServ.processing.next(false);
  }

  checkLoggedInUser() {
    this.dataServ.currentUser.subscribe((user: User) => {
      if (user.username === '') return;

      try {
        this.dataServ.processing.next(true);

        this.http.get('https://api.ipify.org?format=json').subscribe({
          next: (data: any) => {
            const ua = window.navigator.userAgent;
            const browser = Bowser.getParser(window.navigator.userAgent);

            this.deviceCred = {
              browserName: this.encService.encrypt(browser.getBrowserName()),
              browserVersion: this.encService.encrypt(
                browser.getBrowserVersion()
              ),
              ipAddress: this.encService.encrypt(data.ip),
            };
            this.dataServ.processing.next(false);

            // this.verifyUser().subscribe({
            //   next: (res) => {
            //     if (res == true || false) {
            //       const decRole = this.encService.decrypt(
            //         this.currentUser.role
            //       );
            //       if (decRole == '2') {
            //       } else if (decRole == '1') {
            //       }
            //     } else {
            //       this.logOutWithoutCall(false);
            //     }
            //   },
            //   error: (error) => {
            //     this.dataServ.processing.next(false);
            //     this.logOutWithoutCall(false);
            //     console.error(error);
            //   },
            // });
          },
        });
      } catch (error) {
        console.error('Error getting IP address:', error);
      }
    });
  }

  setDeviceCredentials() {
    this.dataServ.processing.next(true);
    this.http.get('https://api.ipify.org?format=json').subscribe({
      next: (data: any) => {
        const ua = window.navigator.userAgent;
        const browser = Bowser.getParser(window.navigator.userAgent);
        this.deviceCred = {
          browserName: this.encService.encrypt(browser.getBrowserName()),
          browserVersion: this.encService.encrypt(browser.getBrowserVersion()),
          ipAddress: this.encService.encrypt(data.ip),
        };
        this.dataServ.processing.next(false);

        // if (this.isLoggingIn == true || true) {
        //   this.verifyUser().subscribe({
        //     next: (res: any) => {
        //       if (res == true) {
        //       }
        //       if (res !== true) {
        //         this.logOutWithoutCall();
        //       }
        //       this.dataServ.processing.next(false);
        //     },
        //     error: (error) => {
        //       this.dataServ.processing.next(false);
        //       this.logOutWithoutCall();
        //       console.error(error);
        //     },
        //   });
        // }
      },
    });
  }

  // verifyUser() {
  //   return this.http.post(`${this.api}account/cookies`, {
  //     ...this.deviceCred,
  //     tokenNo: this.userToken,
    // });
  // }

  get userToken(): string {
    const userCookie = this.cookies.get(this.userKey);
    if (!userCookie) {
      return '';
    }

    try {
      const userData = JSON.parse(userCookie);
      return userData.tokenNo || ''; // Return tokenNo or empty string if it's undefined
    } catch (error) {
      console.error('Failed to parse user cookie:', error);
      return '';
    }
  }
  get headersWithToken(): HttpHeaders {
    const token = this.cookies.get(this.tokenKey);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    return headers;
  }
  get UserNameEnc() {
    return this.currentUser.username;
  }

  get JWTToken(): string {
    return this.cookies.get(this.tokenKey);
  }

  isLoggedIn(): boolean {
    // Check if the JWT token is present
    const token = this.JWTToken;
    const lockoutMessage = localStorage.getItem('lockoutMessage'); // Store lockout message when API returns it

    // if (!token) {
    //   return false;
    // }

    if (!token || lockoutMessage) {
      return false; // Prevent login if account is locked
    }

    try {
      const decodedToken: JwtPayload = jwtDecode(token);

      // Check if the decoded token has an 'exp' field
      if (!decodedToken.exp) {
        return false; // If no expiration field, treat as invalid token
      }

      const isTokenExpired = this.isTokenExpired(decodedToken.exp);

      // If token is not expired, consider the user logged in
      return !isTokenExpired;
    } catch (error) {
      console.error('Error decoding token:', error);
      return false;
    }
  }

  private isTokenExpired(expiration: number): boolean {
    const currentTime = Math.floor(Date.now() / 1000);
    return expiration < currentTime;
  }

  getUserRole(): number {
    return Number(this.encService.decrypt(this.currentUser.role)); // Decrypt and return role
  }
}
